// components/entities/TeamRenderer.tsx
import React, { useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { Activity, Building, Coins, Folder, TrendingUp, Users } from '@/components/ui/lucide-memo';
import UserAvatar from '@/components/UserAvatar';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { formatCreditsInteger } from '#lib/formatCredits';
import type { BaseEntityProps, EntityRowAction, LeasingUserDTO, OrganisationTeamDTO } from '#types/types';
import type { TableColumnDef } from '../portal/Table';
import { portalCard, portalPill } from '../portal/portalTheme';
import styles from '../portal/Table.module.css';
import { EntitySkeleton } from './EntityView';
import { EntityRowActions } from './EntityRowActions';

interface TeamRendererProps extends BaseEntityProps<OrganisationTeamDTO> {
  columns?: TableColumnDef[];
  rowActions?: EntityRowAction<OrganisationTeamDTO>[];
}

const DEFAULT_TEAM_COLUMNS: TableColumnDef[] = [
  { id: 'team', header: '', minWidth: 260 },
  { id: 'members', header: '', minWidth: 120 },
  { id: 'budget', header: '', minWidth: 120 },
  { id: 'remaining', header: '', minWidth: 120 },
  { id: 'actions', header: '', minWidth: 80, align: 'right' },
];

const getTeamBudgetMetrics = (team: OrganisationTeamDTO) => {
  const reserved = team.teamPlan?.reservedCreditsFloat ?? 0;
  const used = team.teamPlan?.usedCreditsFloat ?? 0;
  return {
    reserved,
    used,
    remaining: Math.max(reserved - used, 0),
  };
};

const getUserDisplayName = (member: LeasingUserDTO): string => {
  const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ').trim();
  return fullName || member.email;
};

const formatTeamDate = (value: string | null | undefined): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const findFallbackTeamInOrganisations = (
  organisationRegistry: Record<string, unknown>,
  entityId: string,
): OrganisationTeamDTO | undefined => {
  for (const organisation of Object.values(organisationRegistry)) {
    const teams = (organisation as { teams?: OrganisationTeamDTO[] } | undefined)?.teams;
    const fallback = teams?.find((team) => team.id === entityId);
    if (fallback) return fallback;
  }
  return undefined;
};

const TeamBudgetGrid = ({ metrics, memberCount }: { metrics: ReturnType<typeof getTeamBudgetMetrics>; memberCount: number }) => (
  <div className="grid grid-cols-2 gap-2 text-xs">
    <div className="rounded-lg border border-white/10 bg-white/[0.025] px-2 py-1.5">
      <p className="text-[11px] font-medium text-white/40">Members</p>
      <p className="mt-1 font-mono text-xs text-white/78">{memberCount}</p>
    </div>
    <div className="rounded-lg border border-white/10 bg-white/[0.025] px-2 py-1.5">
      <p className="text-[11px] font-medium text-white/40">Pool</p>
      <p className="mt-1 font-mono text-xs text-white/78">{formatCreditsInteger(metrics.reserved)}</p>
    </div>
    <div className="rounded-lg border border-white/10 bg-white/[0.025] px-2 py-1.5">
      <p className="text-[11px] font-medium text-white/40">Used</p>
      <p className="mt-1 font-mono text-xs text-white/78">{formatCreditsInteger(metrics.used)}</p>
    </div>
    <div className="rounded-lg border border-white/10 bg-white/[0.025] px-2 py-1.5">
      <p className="text-[11px] font-medium text-white/40">Left</p>
      <p className="mt-1 font-mono text-xs text-white/78">{formatCreditsInteger(metrics.remaining)}</p>
    </div>
  </div>
);

const TeamStatTile = ({ icon, label, value, hint }: { icon: React.ReactNode; label: string; value: string | number; hint?: string }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
    <div className="flex items-center justify-between gap-3 text-white/40">
      <span className="text-xs font-medium">{label}</span>
      {icon}
    </div>
    <p className="mt-3 font-mono text-lg font-semibold text-white/88">{value}</p>
    {hint && <p className="mt-1 text-xs text-white/35">{hint}</p>}
  </div>
);

export const TeamRenderer = React.memo(({
  entityId,
  variant = 'row',
  onClick,
  className,
  columns,
  rowActions = [],
}: TeamRendererProps) => {
  const { kernel } = useKernel();
  const teamFromRegistry = useKernelStore((state) => state.inventory.Team.registry[entityId]) as OrganisationTeamDTO | undefined;
  const organisationRegistry = useKernelStore((state) => state.inventory.Organisation.registry) as Record<string, unknown>;
  const userRegistry = useKernelStore((state) => state.inventory.User.registry) as Record<string, LeasingUserDTO | undefined>;
  const team = teamFromRegistry ?? findFallbackTeamInOrganisations(organisationRegistry, entityId);
  const shouldSyncTeamDetail = variant === 'page' && (!teamFromRegistry || !teamFromRegistry.stats);
  const missingMemberIds = useMemo(
    () => variant === 'page' && team ? team.memberIds.filter((memberId) => !userRegistry[memberId]) : [],
    [team, userRegistry, variant],
  );
  const missingOrganisationIds = useMemo(
    () => variant === 'page' && team?.organisationId && !organisationRegistry[team.organisationId] ? [team.organisationId] : [],
    [organisationRegistry, team?.organisationId, variant],
  );

  useEffect(() => {
    if (!shouldSyncTeamDetail || !kernel.current) return;
    void kernel.current.syncEntity('Team', entityId).catch(() => undefined);
  }, [entityId, kernel, shouldSyncTeamDetail]);

  useEffect(() => {
    if (variant !== 'page' || !kernel.current) return;
    if (missingMemberIds.length === 0 && missingOrganisationIds.length === 0) return;
    void Promise.all([
      missingMemberIds.length > 0
        ? kernel.current.ensureInventoryIds('User', missingMemberIds, { force: false, batchSize: 200 })
        : Promise.resolve(null),
      missingOrganisationIds.length > 0
        ? kernel.current.ensureInventoryIds('Organisation', missingOrganisationIds, { force: false, batchSize: 50 })
        : Promise.resolve(null),
    ]).catch(() => undefined);
  }, [kernel, missingMemberIds, missingOrganisationIds, variant]);

  if (!team) return <EntitySkeleton variant={variant} className={className} />;

  const metrics = getTeamBudgetMetrics(team);
  const stats = team.stats ?? null;
  const memberCount = team.memberIds.length;
  const members = team.memberIds.map((memberId) => userRegistry[memberId]).filter((member): member is LeasingUserDTO => Boolean(member));
  const organisationName = team.organisationId
    ? (organisationRegistry[team.organisationId] as { name?: string } | undefined)?.name
    : undefined;

  if (variant === 'chip') {
    return (
      <div className={clsx(portalPill, 'inline-flex items-center gap-2', className)} onClick={onClick}>
        <Users size={12} className="text-white/45" />
        <span className="truncate">{team.name}</span>
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div
        onClick={onClick}
        className={clsx(portalCard, 'rounded-xl p-4 shadow-none', onClick && 'cursor-pointer hover:bg-white/[0.04]', className)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium text-white/50">Team</p>
            <h4 className="mt-2 truncate text-base font-semibold tracking-[-0.03em] text-white/92" title={team.name}>{team.name}</h4>
            {team.description && <p className="mt-1 line-clamp-2 text-xs text-white/45">{team.description}</p>}
          </div>
          <div className="rounded-full border border-white/10 bg-white/[0.03] p-2 text-white/50">
            <Users size={16} />
          </div>
        </div>
        <div className="mt-4">
          <TeamBudgetGrid metrics={metrics} memberCount={memberCount} />
        </div>
      </div>
    );
  }

  if (variant === 'page') {
    return (
      <div className={clsx('h-full min-h-0 overflow-y-auto bg-black text-white', className)}>
        <div className="border-b border-white/10 p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-white/50">Team</p>
              <h1 className="mt-3 truncate text-3xl font-semibold tracking-[-0.05em] text-white/92" title={team.name}>{team.name}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-white/45">
                {organisationName && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1">
                    <Building size={12} />
                    {organisationName}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1">
                  <Users size={12} />
                  {memberCount} member{memberCount === 1 ? '' : 's'}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1">
                  Created {formatTeamDate(team.createdAt)}
                </span>
              </div>
              {team.description && <p className="mt-4 max-w-3xl text-sm leading-6 text-white/55">{team.description}</p>}
            </div>
            <div className="rounded-full border border-[#3B82F6]/20 bg-[#1E3563]/45 p-3 text-[#3B82F6]">
              <Users size={22} />
            </div>
          </div>
        </div>

        <div className="grid gap-4 p-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-4">
            <section className={clsx(portalCard, 'rounded-xl p-4 shadow-none')}>
              <div className="flex items-center gap-2 text-xs font-medium text-white/45">
                <Coins size={14} />
                Budget pool
              </div>
              <div className="mt-4">
                <TeamBudgetGrid metrics={metrics} memberCount={memberCount} />
              </div>
              <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full rounded-full bg-[#3B82F6]"
                  style={{ width: `${metrics.reserved > 0 ? Math.min(100, Math.max(0, (metrics.used / metrics.reserved) * 100)) : 0}%` }}
                />
              </div>
            </section>

            <section className={clsx(portalCard, 'rounded-xl p-4 shadow-none')}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs font-medium text-white/45">
                  <Activity size={14} />
                  Usage audit
                </div>
                {!stats && <span className="text-[10px] text-white/32">Syncing detail</span>}
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <TeamStatTile icon={<Folder size={14} />} label="Projects" value={stats?.totalProjects ?? '-'} />
                <TeamStatTile icon={<Activity size={14} />} label="Generations" value={stats?.totalGenerations ?? '-'} />
                <TeamStatTile icon={<Coins size={14} />} label="Spent" value={stats ? formatCreditsInteger(stats.totalCreditsSpentFloat) : formatCreditsInteger(metrics.used)} />
                <TeamStatTile icon={<TrendingUp size={14} />} label="30 days" value={stats ? formatCreditsInteger(stats.creditsLast30DaysFloat) : '-'} />
              </div>
            </section>
          </div>

          <section className={clsx(portalCard, 'rounded-xl p-4 shadow-none')}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs font-medium text-white/45">
                <Users size={14} />
                Members
              </div>
              <span className="rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5 font-mono text-xs text-white/65">{memberCount}</span>
            </div>
            <div className="mt-4 space-y-2">
              {members.length > 0 ? members.map((member) => (
                <div key={member.id} className="flex min-w-0 items-center gap-3 rounded-xl border border-white/10 bg-white/[0.025] px-3 py-2">
                  <UserAvatar
                    userId={member.id}
                    name={getUserDisplayName(member)}
                    canEdit={false}
                    isOrganisationAdmin={false}
                    size={28}
                    shape="circle"
                    className="!bg-transparent"
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm text-white/82">{getUserDisplayName(member)}</p>
                    <p className="truncate text-xs text-white/38">{member.email}</p>
                  </div>
                </div>
              )) : (
                <p className="rounded-xl border border-white/10 bg-white/[0.025] px-3 py-4 text-center text-xs text-white/35">
                  {memberCount > 0 ? 'Member records are still hydrating.' : 'No members in this team.'}
                </p>
              )}
            </div>
          </section>
        </div>
      </div>
    );
  }

  const cellRenderers: Record<string, React.ReactNode> = {
    team: (
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-white/50">
          <Users size={14} />
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-white/90">{team.name}</div>
          <div className="truncate text-xs text-white/45">{team.description || `${memberCount} member${memberCount === 1 ? '' : 's'}`}</div>
        </div>
      </div>
    ),
    members: <div className="font-mono text-xs text-white/60">{memberCount}</div>,
    budget: <div className="font-mono text-xs text-white/60">{formatCreditsInteger(metrics.reserved)}</div>,
    remaining: <div className="font-mono text-xs text-white/60">{formatCreditsInteger(metrics.remaining)}</div>,
    actions: (
      <div className="flex w-full items-center justify-end">
        <EntityRowActions entityType="Team" entity={team} rowActions={rowActions} />
      </div>
    ),
  };
  const activeColumns = columns || DEFAULT_TEAM_COLUMNS;

  return (
    <div
      onClick={onClick}
      className={clsx(
        styles.gridRow,
        styles.bodyRow,
        'group w-full border-b border-white/5 hover:bg-white/[0.02]',
        onClick && 'cursor-pointer',
        className,
      )}
    >
      {activeColumns.map((column) => (
        <div
          key={column.id}
          className={clsx(styles.cell, column.id === 'actions' && styles.actionCell)}
          style={{
            justifyContent:
              column.id === 'actions' || column.align === 'right'
                ? 'flex-end'
                : column.align === 'center'
                  ? 'center'
                  : 'flex-start',
          }}
        >
          {cellRenderers[column.id] || null}
        </div>
      ))}
    </div>
  );
});
TeamRenderer.displayName = 'TeamRenderer';
